<template><slot></slot></template>

<script lang="ts">
import { defineComponent, onMounted } from "vue";
import { Service } from "@/apps/credit-line/services/network.service";
import { AndroidInterface } from "@/apps/credit-line/services/android.service";
import { ErrorHandler } from "@/apps/credit-line/helpers/errorHandlerWithLogger.helper";
import { useRouter, useRoute } from "vue-router";
import { AxiosError } from "axios";
import logAnalyticsEvent from "@/apps/credit-line/services/google-analytics-events.service";
import AnalyticEvents from "@/shared-helpers/google-analytics-events.helper";

declare const Android: AndroidInterface;

export default defineComponent({
  name: "CreditCardTransitionView",
  setup() {
    const ApiService = new Service();
    const deviceTag = Android.getDeviceTag();
    const router = useRouter();
    const route = useRoute();
    const cardType = (route.query?.type as string) || "physical";

    const navToCardView = () => {
      switch (cardType) {
        case "physical":
          logAnalyticsEvent(
            AnalyticEvents.CreditLine.HomePagePhysicalCardBannerClick,
            "HomePage",
            undefined,
            undefined,
            () => {
              router.push({ name: "CreditCardView" });
            }
          );
          break;
        case "virtual":
          logAnalyticsEvent(
            AnalyticEvents.CreditLine.HomePageVirtualCardBannerClick,
            "HomePage",
            undefined,
            undefined,
            () => {
              router.push({ name: "CreditCardVirtualView" });
            }
          );
          break;
        default:
          logAnalyticsEvent(
            AnalyticEvents.CreditLine.HomePageMessageClick,
            "HomePage",
            undefined,
            undefined,
            () => {
              router.push({ name: "CreditCardView" });
            }
          );
      }
    };

    const navToErrorViewOrDataCapture = (errorCode?: number) => {
      switch (errorCode) {
        // These are specific error codes that imply the need to fix user data
        // We're reditecting to CreditCardUserDataCaptureView to allow users to review and fix their PI
        case 711:
        case 803:
        case 804:
        case 805:
        case 808:
          router.push({
            name: "CreditCardUserDataCaptureView",
            query: { type: cardType },
          });
          break;
        case 807:
          router.push({
            name: "CreditCardUserDataCaptureView",
            query: { type: cardType, skipUserForm: "true" },
          });
          break;
        default:
          navToCardView();
          break;
      }
    };

    onMounted(async () => {
      try {
        const res = await ApiService.createCardUser({
          deviceTag,
        });
        if (res.data.error) {
          navToErrorViewOrDataCapture();
          return;
        }
        navToCardView();
      } catch (error) {
        const axiosError = error as AxiosError;
        ErrorHandler.handleNetworkErrorAndLog(axiosError);
        const errorCode = axiosError?.response?.data?.errorCode;
        navToErrorViewOrDataCapture(errorCode);
      }
    });

    return {};
  },
});
</script>

<style lang="scss" scoped></style>
